import React, {
  FunctionComponent,
  createContext,
  useContext,
  useReducer,
} from 'react';

import { DonationLink } from '../../types/__generated-graphQL__';

type ContextState = {
  isShareModalOpen: boolean;
  isViewModalOpen: boolean;
  isArchiveModalOpen: boolean;
  isRestoreModalOpen: boolean;
  donationLink: DonationLink | null;
};

type Actions =
  | { type: 'openShareModal'; donationLink: DonationLink }
  | { type: 'closeShareModal' }
  | { type: 'openViewModal'; donationLink: DonationLink }
  | { type: 'closeViewModal' }
  | { type: 'openArchiveModal'; donationLink: DonationLink }
  | { type: 'closeArchiveModal' }
  | { type: 'openRestoreModal'; donationLink: DonationLink }
  | { type: 'closeRestoreModal' };

const Context = createContext<{
  state: ContextState;
  dispatch: (action: Actions) => void;
} | null>(null);

export const useDonationLinkContext = () => {
  const contextState = useContext(Context);
  if (contextState === null) {
    throw new Error(
      'useDonationLinkContext must be used within a DonationLinkContextProvider tag',
    );
  }
  return contextState;
};

type DonationLinkContextProviderProps = {
  children: React.ReactNode;
};

export const DonationLinkContextProvider: FunctionComponent<
  DonationLinkContextProviderProps
> = ({ children }) => {
  const initialState = {
    isShareModalOpen: false,
    isViewModalOpen: false,
    isArchiveModalOpen: false,
    isRestoreModalOpen: false,
    donationLink: null,
  };
  const reducer = (state: ContextState, action: Actions) => {
    switch (action.type) {
      case 'openShareModal': {
        return {
          ...state,
          isShareModalOpen: true,
          donationLink: action.donationLink,
        };
      }
      case 'openViewModal': {
        return {
          ...state,
          isViewModalOpen: true,
          donationLink: action.donationLink,
        };
      }
      case 'openArchiveModal': {
        return {
          ...state,
          isArchiveModalOpen: true,
          donationLink: action.donationLink,
        };
      }
      case 'openRestoreModal': {
        return {
          ...state,
          isRestoreModalOpen: true,
          donationLink: action.donationLink,
        };
      }
      case 'closeShareModal':
      case 'closeArchiveModal':
      case 'closeRestoreModal':
      case 'closeViewModal': {
        return initialState;
      }
      default: {
        return initialState;
      }
    }
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <Context.Provider value={{ state, dispatch }}>{children}</Context.Provider>
  );
};
